import { supabase } from './supabase'

const BASE = import.meta.env.VITE_API_BASE || 'https://api.kisos.com'
const IMPERSONATION_TOKEN_KEY = 'kisRxPatientPortalImpersonationToken'
const API_FETCH_TIMEOUT_MS = 12000
const AUTH_SESSION_TIMEOUT_MS = 1500

async function fetchWithTimeout(input, init = {}) {
  if (typeof AbortController === 'undefined' || init.signal) {
    return fetch(input, init)
  }
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), API_FETCH_TIMEOUT_MS)
  try {
    return await fetch(input, { ...init, signal: controller.signal })
  } catch (err) {
    if (err?.name === 'AbortError') {
      const timeoutErr = new Error('Patient portal request timed out. Please refresh and try again.')
      timeoutErr.status = 408
      throw timeoutErr
    }
    throw err
  } finally {
    window.clearTimeout(timeout)
  }
}

export function getImpersonationToken() {
  if (typeof window === 'undefined') return ''
  return window.sessionStorage.getItem(IMPERSONATION_TOKEN_KEY) || ''
}

export function setImpersonationToken(token) {
  if (typeof window === 'undefined') return
  const cleanToken = String(token || '').trim()
  if (cleanToken) window.sessionStorage.setItem(IMPERSONATION_TOKEN_KEY, cleanToken)
  else window.sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY)
}

export function clearImpersonationToken() {
  setImpersonationToken('')
}

export function isImpersonatingPatient() {
  return Boolean(getImpersonationToken())
}

function normalizeExpiresAt(value) {
  const numericValue = Number(value || 0)
  if (!Number.isFinite(numericValue) || numericValue <= 0) return 0
  return numericValue > 10000000000 ? Math.floor(numericValue / 1000) : numericValue
}

function tokenIsUsable(expiresAt) {
  const normalized = normalizeExpiresAt(expiresAt)
  if (!normalized) return true
  return normalized > Math.floor(Date.now() / 1000) + 30
}

function extractSession(value) {
  if (!value || typeof value !== 'object') return null
  return value.currentSession || value.session || value
}

function authStorageKeys(storage) {
  const configuredUrl = String(import.meta.env.VITE_SUPABASE_URL || '')
  const projectRef = configuredUrl.match(/^https?:\/\/([^.]+)/i)?.[1]
  const expectedKey = projectRef ? `sb-${projectRef}-auth-token` : ''
  const keys = []

  if (expectedKey && storage.getItem(expectedKey)) keys.push(expectedKey)

  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index)
    if (key && key !== expectedKey && key.startsWith('sb-') && key.endsWith('-auth-token')) keys.push(key)
  }

  return keys
}

function readCachedAccessToken() {
  if (typeof window === 'undefined') return null
  let stores
  try {
    stores = [window.localStorage, window.sessionStorage].filter(Boolean)
  } catch {
    return null
  }

  for (const storage of stores) {
    try {
      for (const key of authStorageKeys(storage)) {
        const session = extractSession(JSON.parse(storage.getItem(key) || 'null'))
        const token = session?.access_token
        if (token && tokenIsUsable(session?.expires_at || session?.expiresAt)) return token
      }
    } catch {
      // Ignore malformed or unavailable browser storage and fall back to Supabase.
    }
  }
  return null
}

function withTimeout(promise, timeoutMs) {
  let timeoutId = null
  const timeout = new Promise(resolve => {
    timeoutId = window.setTimeout(() => resolve(null), timeoutMs)
  })
  return Promise.race([promise, timeout]).finally(() => {
    if (timeoutId) window.clearTimeout(timeoutId)
  })
}

async function authHeaders() {
  const impersonationToken = getImpersonationToken()
  if (impersonationToken) return { 'X-KIS-Rx-Impersonation': impersonationToken }
  const cachedToken = readCachedAccessToken()
  if (cachedToken) return { Authorization: `Bearer ${cachedToken}` }
  const result = await withTimeout(supabase.auth.getSession(), AUTH_SESSION_TIMEOUT_MS)
  const token = result?.data?.session?.access_token || readCachedAccessToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

function errorMessage(method, path, status, text) {
  let json = null
  try { json = text ? JSON.parse(text) : null } catch { /* keep raw text */ }
  return json?.message || json?.error || `${method} ${path} failed (${status})`
}

export async function apiGet(path) {
  const headers = await authHeaders()
  const res = await fetchWithTimeout(`${BASE}${path}`, {
    headers: { ...headers, 'Cache-Control': 'no-cache' },
    cache: 'no-store',
  })
  if (!res.ok) {
    const txt = await res.text().catch(() => '')
    const err = new Error(errorMessage('GET', path, res.status, txt))
    err.status = res.status
    err.body = txt
    throw err
  }
  return res.json()
}

export async function apiPost(path, body) {
  const headers = { ...(await authHeaders()), 'Content-Type': 'application/json' }
  const res = await fetchWithTimeout(`${BASE}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body || {}),
    cache: 'no-store',
  })
  if (!res.ok) {
    const txt = await res.text().catch(() => '')
    throw new Error(errorMessage('POST', path, res.status, txt))
  }
  return res.json()
}

export async function apiPatch(path, body) {
  const headers = { ...(await authHeaders()), 'Content-Type': 'application/json' }
  const res = await fetchWithTimeout(`${BASE}${path}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(body || {}),
    cache: 'no-store',
  })
  if (!res.ok) {
    const txt = await res.text().catch(() => '')
    throw new Error(errorMessage('PATCH', path, res.status, txt))
  }
  return res.json()
}

export async function apiDelete(path, body) {
  const headers = { ...(await authHeaders()), 'Content-Type': 'application/json' }
  const res = await fetchWithTimeout(`${BASE}${path}`, {
    method: 'DELETE',
    headers,
    body: body ? JSON.stringify(body) : undefined,
    cache: 'no-store',
  })
  if (!res.ok) {
    const txt = await res.text().catch(() => '')
    throw new Error(errorMessage('DELETE', path, res.status, txt))
  }
  return res.json()
}

export async function apiUpload(path, formData) {
  const headers = await authHeaders()
  const res = await fetchWithTimeout(`${BASE}${path}`, { method: 'POST', headers, body: formData })
  if (!res.ok) {
    const txt = await res.text().catch(() => '')
    throw new Error(errorMessage('POST', path, res.status, txt))
  }
  return res.json()
}

// Public (no auth) - used during invite redemption
export async function publicGet(path) {
  const res = await fetchWithTimeout(`${BASE}${path}`)
  if (!res.ok) throw new Error(`GET ${path} failed (${res.status})`)
  return res.json()
}

export async function publicPost(path, body) {
  const res = await fetchWithTimeout(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {})
  })
  if (!res.ok) {
    const txt = await res.text().catch(() => '')
    let json = null
    try { json = txt ? JSON.parse(txt) : null } catch { /* keep text */ }
    const err = new Error(errorMessage('POST', path, res.status, txt))
    err.fields = json?.fields || null
    err.status = res.status
    throw err
  }
  return res.json()
}
